<template>
  <div class="product__images">
    <div class="product-block__wishlist" style="
        border-radius: 50%;
        background: #ffffffb3;
        width: 40px;
        height: 40px;
    ">
      <HeartIcon style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
      " :class="{ 'product-block__wishlist--active': isProductWishlistActive }"
        @click="saveProductWishlist(productStore.product)"></HeartIcon>
    </div>

    <ProductMainSliderVue :media="productMedia" :selected-slide-index="selectedSlideIndex"
      @change="updateSelectedSlideIndex" />
    <client-only>
      <LeverSphereProductThumbnailSliderVue v-if="isLeverSphereCustomizer" :images="productThumbnailImages" :selected-slide-index="selectedSlideIndex"
        :scroll-to-left="isThumbnailSliderScrollToLeft" @change="updateSelectedSlideIndex" />
      <ProductThumbnailSliderVue v-else :images="productThumbnailImages" :selected-slide-index="selectedSlideIndex"
        :scroll-to-left="isThumbnailSliderScrollToLeft" @change="updateSelectedSlideIndex" />
      <template #placeholder>
        <div class="product__thumbnail-slider">
          <sib-skeleton-loading
            v-for="index in 4"
            :key="index"
            size="square"
            round="sm"
            class="product__thumbnail-skeleton"
          ></sib-skeleton-loading>
        </div>
      </template>
    </client-only>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from '@vue/reactivity'
import { onMounted, onUnmounted, watch } from 'vue'
import useProductStore from '@/store/product'
import useABTestStoreV2 from '@/composables/abtest/store'

import ProductMainSliderVue from './ProductMainSlider.vue'
import ProductThumbnailSliderVue from './ProductThumbnailSlider.vue'
import LeverSphereProductThumbnailSliderVue from './LeverSphereProductThumbnailSlider.vue'
import { isProductHasTags } from '@/utils/product'
import HeartIcon from '@/assets/images/icons/icon-heart.svg'
import CacheService, { getJSONfromCacheResponse } from '@/services/cache'
import EventBus from '@/services/eventbus'
import {
  AB_TEST_CODE_SHOW_VIDEO_ON_PRODUCT_IMAGES,
  EVENT_ABTEST_ASSIGNED,
  EVENT_PRODUCT_SHOW_PREVIEW,
  CACHE_KEY_WISH_LIST_ITEMS,
  ONE_YEAR_IN_MILLISECONDS,
  EVENT_SAVE_PRODUCT_WISH_LIST,
  cloneDeep,
  MIXPANEL_DESTINATION,
  destinationIntegrations,
} from '@/utils'
import { useABTest } from '@/composables/useABTest'
import Analytics from '@/services/analytics'
import Logger from '@/services/log'
import useDeviceStore from '@/store/device'
const deviceStore = useDeviceStore()
const productStore = useProductStore()
const { getABTestActivedVariantByCode } = useABTest()

const isLeverSphereCustomizer = computed(() => {
  return !!productStore.productTemplateConfig && isProductHasTags(productStore.product, ['leversphere_customizer'])
})

const abtestVariantShowVideoOnProductImages = computed(() => {
  const abtestVariant = getABTestActivedVariantByCode(
    AB_TEST_CODE_SHOW_VIDEO_ON_PRODUCT_IMAGES
  )
  return abtestVariant
})

const isThumbnailSliderScrollToLeft = computed(() => {
  return (
    (productStore.isUILandingProduct || productStore.isProductUINewVersion) &&
    deviceStore.isPhone
  )
})

const abtestStore = useABTestStoreV2()
const isMounted = ref(false)

const productMedia = computed(() => {
  let mediaList = productStore.product?.media || []
  return mediaList
})

const productThumbnailImages = computed(() => {
  if (!productMedia.value) return []
  return productMedia.value.map((item) => {
    return {
      image: Object.assign({}, item.image, {
        id: null,
        url: null,
      }),
      mediaContentType: item.mediaContentType,
    }
  })
})
const selectedSlideIndex = ref(0)

const selectedVariant = computed(() => {
  return productStore.selectedVariant
})

const productWishlist = ref<any[]>([])

const isProductWishlistActive = computed(() =>
  productWishlist.value.find(
    (product: any) => product.handle?.split('/')?.pop()?.split('?')[0] == productStore.product?.handle?.split('/')?.pop()?.split('?')[0]
  )
)

let unwatchHasPreview: (() => void) | null = null

onMounted(async () => {
  isMounted.value = true

  unwatchHasPreview = watch(
    () => productStore.hasPreview,
    (hasPreview) => {
      try {
        if (!hasPreview && selectedSlideIndex.value === productMedia.value.length) {
          updateSelectedSlideIndex(0)
        }
      } catch (error) {
        console.error('Error inside hasPreview watch callback', error)
      }
    }
  )

  watch([selectedVariant, () => abtestStore.isReady], ([newVariant, isReady]) => {
    if (!isReady) return
    // do not change the slide when current selected slide is the preview slide
    if (selectedSlideIndex.value === productMedia.value.length) return

    // do not change the slide if user did not choose a variant
    if (!productStore.didUserChooseVariant) return

    if (!selectedVariant.value?.image?.id) return

    const images = productMedia.value.map((item) => item.image)
    if (!images.length) return
    const index = images.findIndex(
      (img) => img.id === selectedVariant.value?.image?.id
    )
    if (index === -1) return
    updateSelectedSlideIndex(index)
  })
  EventBus.on(EVENT_PRODUCT_SHOW_PREVIEW, productShowPreview)

  trackingABTestShowVideoOnProductImages()

  // Block users from right clicking and saving images
  document
    .querySelector('.product__images')
    ?.addEventListener('contextmenu', (event) => event.preventDefault())

  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(async () => {
  productWishlist.value = await getProductWishlist()
    })
  } else {
    setTimeout(async () => {
      productWishlist.value = await getProductWishlist()
    }, 200)
  }
})

onUnmounted(() => {
  try {
  if (unwatchHasPreview) unwatchHasPreview()
  EventBus.off(EVENT_PRODUCT_SHOW_PREVIEW, productShowPreview)
  } catch (error) {
  }
})

function productShowPreview() {
  // update image slider in case the user update personalize
  updateSelectedSlideIndex(productMedia.value.length)
}

function updateSelectedSlideIndex(index: number) {
  selectedSlideIndex.value = index
}

function trackingABTestShowVideoOnProductImages() {
  if (abtestVariantShowVideoOnProductImages.value === null) return
  const payload = {
    name: AB_TEST_CODE_SHOW_VIDEO_ON_PRODUCT_IMAGES,
    variant: abtestVariantShowVideoOnProductImages.value,
  }

  Analytics.track(
    EVENT_ABTEST_ASSIGNED,
    payload,
    destinationIntegrations([MIXPANEL_DESTINATION])
  )
  Logger.log('Assigned AB Test', payload, 1)
}


async function saveProductWishlist(product: any) {
  try {
    if (!product) return
    const wishlistItems = await getProductWishlist()
    const index = wishlistItems.findIndex(
      (item) => item.handle?.split('/')?.pop()?.split('?')[0] === product.handle?.split('/')?.pop()?.split('?')[0]
    )

    if (index > -1) wishlistItems.splice(index, 1)
    else wishlistItems.unshift(product)

    productWishlist.value = wishlistItems

    EventBus.trigger(EVENT_SAVE_PRODUCT_WISH_LIST)

    await CacheService.instance?.set(
      CACHE_KEY_WISH_LIST_ITEMS,
      cloneDeep(wishlistItems),
      ONE_YEAR_IN_MILLISECONDS
    )
  } catch (error: any) {
    Logger.error('Error when saving product wish list to cache', {
      error,
      product,
    })
    Analytics.error(error)
  }
}

async function getProductWishlist() {
  const wishlistItemsFromCache = await CacheService.instance?.get(
    CACHE_KEY_WISH_LIST_ITEMS
  )
  const wishlistItems: any[] =
    getJSONfromCacheResponse(wishlistItemsFromCache) || []

  return wishlistItems
}
</script>

<style lang="scss">
.product__images {
  overflow: hidden;
  display: flex;
  position: relative;
  margin-top: 1.5em;
  --padding-left: 110px;
  @include media-md-up {
    position: sticky;
    top: calc(var(--fixed-content-height) + 1em);
    margin-top: 0;
  }
}

@include media-up(501px) {
  .product__images {
    padding-left: var(--padding-left);
  }
}

@include media-md-up {
  .product__images {
    padding-left: 0;
  }
}

@include media-lg-up {
  .product__images {
    padding-left: var(--padding-left);
  }
}
</style>